// A boundary scoped to ONE lazily-loaded section.
//
// The tempting design is a global overlay plus window error listeners. That
// does not work: a lazy import that rejects throws during render, and React
// unmounts up to the nearest boundary regardless of what any window listener
// does. If that boundary sits above an edited form, the form unmounts, its
// useUnsavedGuard cleanup releases the hold, and the reload guard then
// believes there is nothing to protect — the exact failure the guard exists
// to prevent.
//
// So the boundary goes AROUND THE FAILING SECTION ONLY. Everything beside it
// keeps its subtree, its React state and its holds. Place one of these
// around each lazily-loaded region; never around a whole page that also
// contains forms.
import React from 'react';

interface Props {
  children: React.ReactNode;
  /** Rendered in place of this section only. */
  fallback: (error: Error) => React.ReactNode;
  /** Told that this section failed, so a page-level notice can appear
   *  without unmounting anything. */
  onError?: (error: Error) => void;
}

interface State { error: Error | null }

export class LazySection extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    this.props.onError?.(error);
  }

  render() {
    if (this.state.error) return this.props.fallback(this.state.error);
    return this.props.children;
  }
}

/** Chunk-load failures look different per browser and bundler; this is the
 *  union of the shapes seen in practice. Anything else is a real bug and
 *  must not be dressed up as "reload to fix". */
export const isChunkLoadError = (e: unknown): boolean => {
  const msg = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
  return /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|expected a JavaScript(-or-Wasm)? module/i
    .test(msg);
};
